// src/App.js
import React, { useState, useEffect } from "react";
import Overview from "./components/Overview";
import MacroSection from "./sections/MacroSection";
import SentimentSection from "./components/SentimentSection";
import FlowsSection from "./sections/FlowsSection";
import EarningsSection from "./sections/EarningsSection";
import EventsSection from "./sections/EventsSection";
import AboutSection from "./components/AboutSection";
import "./App.css";

const App = () => {
  const [currentTab, setCurrentTab] = useState("overview");
  const [currentTime, setCurrentTime] = useState("");
  const [theme, setTheme] = useState("light");
  const [isFredConnected, setIsFredConnected] = useState(false);
  const [showWarmup, setShowWarmup] = useState(false);

  const tabs = [
    { id: "overview", label: "Market Overview", icon: "📊" },
    { id: "macro", label: "Macro Data", icon: "📈" },
    { id: "sentiment", label: "Sentiment & Risk", icon: "🎯" },
    { id: "flows", label: "Flows & Liquidity", icon: "💧" },
    { id: "earnings", label: "Earnings Calendar", icon: "💰" },
    { id: "events", label: "Macro Events", icon: "📅" },
    { id: "about", label: "About", icon: "ℹ️" },
  ];

  // Load theme from localStorage or system preference
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
    setTheme(savedTheme || systemTheme);
  }, []);

  // Update time
  useEffect(() => {
    const updateTime = () => setCurrentTime(new Date().toLocaleTimeString());
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

      // Render free tier may sleep: show a warm-up notice until we connect
    useEffect(() => {
      let showTimer;

      if (!isFredConnected) {
        // wait a bit so it doesn't flash on fast loads
        showTimer = setTimeout(() => setShowWarmup(true), 800);
      } else {
        setShowWarmup(false);
      }

      // Safety fallback: don't show forever
      const kill = setTimeout(() => setShowWarmup(false), 90000);

      return () => {
        clearTimeout(showTimer);
        clearTimeout(kill);
      };
    }, [isFredConnected]);

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
  };

  const handleFredConnection = (connected) => {
    setIsFredConnected(connected);
  };

  const renderContent = () => {
    switch (currentTab) {
      case "overview":
        return <Overview onConnectionStatus={handleFredConnection} />;
      case "macro":
        return <MacroSection onConnectionStatus={handleFredConnection} />;
      case "sentiment":
        return <SentimentSection />;
      case "flows":
        return <FlowsSection onConnectionStatus={handleFredConnection} />;
      case "earnings":
        return <EarningsSection onConnectionStatus={handleFredConnection} />;
      case "events":
        return <EventsSection theme={theme} />;
      case "about":
        return <AboutSection />;
      default:
        return <Overview onConnectionStatus={handleFredConnection} />;
    }
  };

  return (
    <div className={`app-root theme-${theme}`}>
      <div className="container">
        <header className="header">
          <div className="header-main">
            <div className="header-brand">
              <img className="header-logo" src="/brand-logo.png" alt="InvestsToday logo" />
              <div className="header-text">
                <h1>InvestsToday</h1>
                <p>Real-time market data and institutional-grade analytics</p>
              </div>
            </div>

            <div className="header-right">
              {showWarmup && !isFredConnected && (
                <div
                  className="warmup-pill"
                  title="First load can be slower when the server is waking up"
                >
                  First load may take ~60s while the server starts.
                </div>
              )}

              {/* Theme toggle */}
              <button className="theme-toggle" onClick={toggleTheme}>
                <div className="theme-toggle-track">
                  <span className={`theme-toggle-label ${theme === "light" ? "active" : ""}`}>
                    Light
                  </span>
                  <span className={`theme-toggle-label ${theme === "dark" ? "active" : ""}`}>
                    Dark
                  </span>
                  <div className={`theme-toggle-thumb ${theme === "dark" ? "right" : "left"}`} />
                </div>
              </button>
            </div>
          </div>

          <div className="live-indicator">
            <div className="pulse-dot"></div>
            <span>Live data • Updated {currentTime}</span>
            <span className={`api-status ${isFredConnected ? "api-connected" : "api-disconnected"}`}>
              FRED: {isFredConnected ? "Connected" : "Disconnected"}
            </span>
          </div>
        </header>

        <nav className="tabs-wrap" aria-label="Dashboard sections">
          <div className="tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`tab-button ${currentTab === tab.id ? "active" : ""}`}
                onClick={() => setCurrentTab(tab.id)}
              >
                <span className="tab-icon">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>

          <div className="tabs-hint">Swipe →</div>
          <div className="tabs-tip">Tip: Click any card to open the full chart.</div>
        </nav>

        <main className="content">{renderContent()}</main>

        <footer className="footer">
          <p>Data sources: FRED, U.S. Treasury FiscalData</p>
          <p>⚠️ This dashboard uses real APIs; values may be delayed or approximate.</p>
        </footer>
      </div>
    </div>
  );
};

export default App;